// ----------------------------------------------------------------------
// (c) Copyright 2020, Dark Star Systems, Inc.  All rights reserved.
// This file may contain proprietary and confidential information.
// DO NOT COPY or distribute in any form without prior written consent.
// ----------------------------------------------------------------------

import {Object3D, Vector2, Vector3, Matrix4} from 'three'
import type {Viewer} from 'ngl'
import {degToRad, radToDeg} from './utils'

const tmpMatrix = new Matrix4()
const tmpVector2 = new Vector2()
const tmpVector3 = new Vector3()

interface CameraRigParams {
  cameraDistance: number;
}

export const defaultCameraParams: CameraRigParams = {
  cameraDistance: 3
}

/** A camera rig:
    The base can be moved (translated) around (in XZ) and/or rotate. Only use one of polar or rectangular.
    - baseR & baseTheta: polar coord position
    - baseX & baseY: rectangular coord position
    The stand is parented to the base and translates rig up & down (Y)
    - standHeight: height of stand
    The boom is parented to the stand and rotates up & down around the attachment point, and scales in & out.
    - default boom is along X axis
    - boomLength: length of boom
    - boomRotation: rotation around local Y axis, 0 means pointing forward (radians)
    - boomElevation: rotation around local Z axis, 0 means horizontal (radians)
    The camera itself is parented to the end of the boom.
    The camera always points toward the lookAt object.
*/
export class CameraRig {
  cameraBase: Object3D
  cameraStand: Object3D // represents the head of the stand
  cameraBoomJoint: Object3D // "shoulder" joint between stand and boom
  cameraBoom: Object3D
  cameraLookAt: Vector3
  cameraRoll: number            // radians
  viewer?: Viewer

  constructor(camParams?: Partial<CameraRigParams>) {
    const params: CameraRigParams = {...defaultCameraParams, ...camParams}

    this.cameraRoll = 0

    this.cameraLookAt = new Vector3() // default to 0,0,0
    this.cameraBase = new Object3D()
    this.cameraBase.name = 'cameraRigBase'
    this.cameraBase.translateZ(params.cameraDistance) // start on -Z axis

    this.cameraStand = new Object3D()
    this.cameraStand.name = 'cameraRigStand'
    this.cameraStand.parent = this.cameraBase
    this.cameraStand.position.y = 0

    this.cameraBoomJoint = new Object3D()
    this.cameraBoomJoint.name = 'cameraBoomJoint'
    this.cameraBoomJoint.parent = this.cameraStand

    this.cameraBoom = new Object3D()
    this.cameraBoom.name = 'cameraRigBoom'
    this.cameraBoom.parent = this.cameraBoomJoint
  }

  setup(viewer: Viewer) {
    this.viewer = viewer
    // parent camera to our rig and point it to the lookAt object
    viewer.perspectiveCamera.position.set(0, 0, 0)
    viewer.perspectiveCamera.parent = this.cameraBoom
    viewer.perspectiveCamera.lookAt(this.cameraLookAt)
    this.setLookAt()

    const setCameraDistance = (d: number) => {
      this.updateCameraRig({baseR: d})
    }
    const getCameraDistance = () => {
      viewer.camera.getWorldPosition(tmpVector3)
      return tmpVector3.length()
    }
    // replace cameraDistance setter & getter with bound callbacks
    Object.defineProperty(viewer, 'cameraDistance', {
      get: getCameraDistance.bind(this),
      set: setCameraDistance.bind(this),
      enumerable: true,
      configurable: true
    })
  }

  serialize() {
    return {
      base: {position: this.cameraBase.position.clone(),
             rotation: this.cameraBase.quaternion.clone()},
      stand: {position: this.cameraStand.position.clone(),
              rotation: this.cameraStand.quaternion.clone()},
      boom: {position: this.cameraBoom.position.clone(),
             rotation: this.cameraBoom.quaternion.clone()},
      lookAt: this.cameraLookAt.clone(),
      roll: this.cameraRoll
    }
  }

  /* Camera Rig: base, stand, boom, roll, and lookAt point */

  cameraRigToStr() {
    tmpVector2.set(this.cameraBase.position.x, this.cameraBase.position.z)
    // Why not some Unicode chars to shorten the printout (angle, perpendicular, theta, phi),
    return `base x,z: ${tmpVector2.x.toFixed(2)}, ${tmpVector2.y.toFixed(2)}, ` +
      `r,\u2220: ${tmpVector2.length().toFixed(2)}, ${radToDeg(tmpVector2.angle()).toFixed(1)}\u00b0; ` +
      `stand ht: ${this.cameraStand.position.y.toFixed(2)}; ` +
      `boom len: ${this.cameraBoom.position.y.toFixed(2)}, ` +
      `\u03b8:${radToDeg(this.cameraBoom.rotation.y).toFixed(1)}, \u03c6:${radToDeg(this.cameraBoom.rotation.z).toFixed(1)}; ` +
      `roll: ${radToDeg(this.cameraRoll).toFixed(1)}; ` +
      `lookAt: ${this.cameraLookAt.toArray().map(x => x.toFixed(2))}`
  }

  /** Update the camera rig.
      Base position is cartesian. All angles in radians.
      Pass {deferUpdate: true} if making multiple calls in a row, for all except last one
      (or just call `setLookAt()` afterwards).
      As a special case, passing `{baseR: N}` updates radius (distance from origin).
      Returns true if anything changed.
      */
  updateCameraRig(params: {
    baseX?: number;
    baseY?: number;
    baseR?: number;             // special case: only update radius
    standHeight?: number;
    boomLength?: number;
    boomRotation?: number;
    boomElevation?: number;     // above horizontal
    cameraRoll?: number;
    cameraLookAt?: Vector3;
    deferUpdate?: boolean;
  }) {
    const initialRig = JSON.stringify(this.serialize())
    // Note: this code setting up the objects should agree with the code in CameraRigView.ts
    if (params.baseR !== undefined) {
      const polar = this.cameraBasePolar()
      const theta = polar[1]
      params.baseX = params.baseR * Math.cos(theta)
      params.baseY = params.baseR * Math.sin(theta)
    }
    if (params.baseX !== undefined)
      this.cameraBase.position.x = params.baseX
    if (params.baseY !== undefined)
      this.cameraBase.position.z = params.baseY
    if (params.standHeight !== undefined)
      this.cameraStand.position.set(0, params.standHeight, 0)
    if (params.boomRotation !== undefined)
      this.cameraStand.rotation.set(0, params.boomRotation, 0) // rotate stand around local Y axis to rotate boom
    if (params.boomLength !== undefined)
      this.cameraBoom.position.set(0, params.boomLength, 0)
    if (params.boomElevation !== undefined)
      this.cameraBoomJoint.rotation.set(Math.PI*0.5 - params.boomElevation, 0, 0)
    if (params.cameraRoll)
      this.cameraRoll = params.cameraRoll
    if (params.cameraLookAt)
      this.cameraLookAt = params.cameraLookAt

    if (!params.deferUpdate)
      this.setLookAt()

    // return true if anything changed
    return (initialRig != JSON.stringify(this.serialize()))
  }

  /** Set the camera's lookAt point.
      With no arg, just updates the camera to point at the current cameraLookAt vector.
  */
  setLookAt(point?: Vector3 | number[]) {
    if (point instanceof Vector3)
      this.cameraLookAt.copy(point)
    else if (point !== undefined)
      this.cameraLookAt.fromArray(point)
    this.viewer?.camera.lookAt(this.cameraLookAt)
    // have to apply roll after lookAt: rotate around Z
    this.viewer?.camera.rotateZ(this.cameraRoll)
  }

  /** Returns [r, theta] */
  cameraBasePolar() {
    tmpVector2.set(this.cameraBase.position.x, this.cameraBase.position.z)
    return [tmpVector2.length(), tmpVector2.angle()]
  }
}

export class CameraRigFree {
  theta: number
  phi: number
  dolly: number

  cameraLookAt: Vector3
  base: Object3D
  boom: Object3D

  viewer?: Viewer

  constructor(camParams?: Partial<CameraRigParams>) {
    const params: CameraRigParams = {...defaultCameraParams, ...camParams}

    this.theta = 0
    this.phi = 0
    this.dolly = params.cameraDistance

    // at origin, or maybe at lookAt?
    this.base = new Object3D()
    this.base.name = 'cameraRigBase'
    this.boom = new Object3D()
    this.boom.name = 'cameraRigBoom'
    this.boom.parent = this.base

    this.cameraLookAt = new Vector3() // default to 0,0,0
  }

  setup(viewer: Viewer) {
    this.viewer = viewer
    // parent camera to our rig and point it to the lookAt object
    viewer.perspectiveCamera.position.set(0, 0, 0)
    viewer.perspectiveCamera.parent = this.boom
    viewer.perspectiveCamera.lookAt(this.cameraLookAt)
    this.setLookAt()

    const setCameraDistance = (d: number) => {
      this.updateCameraRig({dolly: d})
    }
    const getCameraDistance = () => {
      viewer.camera.getWorldPosition(tmpVector3)
      return tmpVector3.length()
    }
    // replace cameraDistance setter & getter with bound callbacks
    Object.defineProperty(viewer, 'cameraDistance', {
      get: getCameraDistance.bind(this),
      set: setCameraDistance.bind(this),
      enumerable: true,
      configurable: true
    })
  }

  serialize() {
    return {
      theta: this.theta,
      phi: this.phi,
      dolly: this.dolly
    }
  }

  cameraRigToStr() {
    // unicode here is theta and phi
    return `\u03b8:${radToDeg(this.theta).toFixed(1)}, \u03c6:${radToDeg(this.phi).toFixed(1)}; ` +
      `dolly: ${this.dolly.toFixed(1)}; ` +
      `lookAt: ${this.cameraLookAt.toArray().map(x => x.toFixed(2))}`
  }

  /** Update the camera rig.
      Pass {deferUpdate: true} if making multiple calls in a row, for all except last one
      (or just call `setLookAt()` afterwards).
      */
  updateCameraRig(params: {
    theta?: number;
    phi?: number;
    dolly?: number;
    cameraLookAt?: Vector3;
    deferUpdate?: boolean;
  }) {
    const initialRig = JSON.stringify(this.serialize())
    if (params.theta !== undefined)
      this.theta = params.theta
    if (params.phi !== undefined)
      this.phi = params.phi
    if (params.dolly !== undefined)
      this.dolly = params.dolly
    // default boom is along the -Z axis (like NGL), so we set its Z position
    this.boom.position.set(0, 0, -this.dolly)
    // theta is rotation around Y (in the base XZ plane), and phi is elevation above XZ plane.
    // Since boom is along -Z, elevation comes from rotating around X.
    this.base.rotation.set(this.phi, this.theta, 0, 'ZYX')
    if (params.cameraLookAt)
      this.cameraLookAt = params.cameraLookAt
    if (!params.deferUpdate)
      this.setLookAt()
    // return true if anything changed
    return (initialRig != JSON.stringify(this.serialize()))
  }

  /** Set the camera's lookAt point.
      With no arg, just updates the camera to point at the current cameraLookAt vector.
  */
  setLookAt(point?: Vector3 | number[]) {
    if (point instanceof Vector3)
      this.cameraLookAt.copy(point)
    else if (point !== undefined)
      this.cameraLookAt.fromArray(point)
    this.viewer?.camera.lookAt(this.cameraLookAt)
  }
}
